<template>
  <div class="page-main flex-page">
    <!--START: Header Wrapper-->
    <div
      class="header-wrapper"
      :class="{ 'show-shadow': showOptions.headerShadow }"
    >
      <!--START: Page Header-->
      <RegularHeader
        class="regular-header"
        :data="regularHeader"
      ></RegularHeader>
      <!--END: Page Header-->
    </div>
    <!--END: Header Wrapper-->

    <!--START: Loader-->
    <PageLoader v-if="lodash.isEmpty(company)" :showLoader="true"></PageLoader>
    <!--END: Loader-->

    <!--START: Apps-->
    <div ref="settings" class="settings-scroll-block" @scroll="settingsScroll">
      <div class="server-location-wrapper settings-form card">
        <div class="info-wrapper">
          <h3 class="section-title">Data server locations</h3>
          <p>Details of where and how your data is handled by Macha</p>
        </div>
        <div v-for="s in servers" :key="s._id" class="server-item">
          <div class="server-info">
            <h3>{{ s.title }}</h3>
            <p>{{ s.description }}</p>
          </div>
          <div class="server-details">
            <span class="server-location">{{ s.location }}</span>
            <img :src="getCountryFlag(s.country)" :alt="s.country" />
          </div>
        </div>
      </div>

      <div class="security-document">
        <unicon name="lock"></unicon>
        <span
          >Learn how Macha secures and stores your data -
          <a
            href="https://www.getmacha.com/policies/security.pdf"
            target="_blank"
            >Macha Security Policy</a
          ></span
        >
      </div>

      <!--START: Password Management Section-->
      <div class="password-management-wrapper settings-form card">
        <div class="info-wrapper">
          <h3 class="section-title">Password Settings</h3>
          <p>Manage your account password for alternative login</p>
        </div>

        <!--START: Update Own Password-->
        <div class="password-form">
          <h4>Update Your Password</h4>
          <div class="form-group" v-if="hasPassword">
            <label>Current Password</label>
            <input
              type="password"
              v-model="passwordForm.oldPassword"
              placeholder="Enter current password"
            />
          </div>
          <div class="form-group">
            <label>New Password</label>
            <input
              type="password"
              v-model="passwordForm.newPassword"
              placeholder="Enter new password"
            />
          </div>
          <div class="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              v-model="passwordForm.confirmPassword"
              placeholder="Confirm new password"
            />
          </div>
          <button
            class="btn btn-small btn-primary"
            @click="updateOwnPassword"
            :disabled="!canUpdatePassword"
          >
            {{ hasPassword ? "Update Password" : "Set Password" }}
          </button>
        </div>
        <!--END: Update Own Password-->

        <!--START: Admin Set Password-->
        <div v-if="isSuperAdmin" class="password-form admin-password-form">
          <h4>Set Password for Another User (Admin Only)</h4>
          <p class="admin-note">
            As admin, you can set a temporary password for users who cannot
            receive OTP emails.
          </p>
          <div class="form-group">
            <label>User Email</label>
            <input
              type="email"
              v-model="adminPasswordForm.targetEmail"
              placeholder="Enter user's email"
            />
          </div>
          <div class="form-group">
            <label>New Password</label>
            <input
              type="password"
              v-model="adminPasswordForm.newPassword"
              placeholder="Enter password for user"
            />
          </div>
          <button
            class="btn btn-small btn-primary"
            @click="setUserPassword"
            :disabled="!canSetUserPassword"
          >
            Set User Password
          </button>
        </div>
        <!--END: Admin Set Password-->
      </div>
      <!--END: Password Management Section-->

      <div class="account-actions">
        <button
          class="btn btn-small btn-primary"
          @click="sendNotification('delete')"
        >
          Delete Account
        </button>
        <button
          class="btn btn-small btn-border"
          @click="sendNotification('download')"
        >
          Download Data
        </button>
      </div>
    </div>
    <!--END: Apps-->

    <!--START: Notification Message-->
    <NotificationMessage
      :show="status.show"
      :status="status.status"
      :title="status.title"
    ></NotificationMessage>
    <!--END: Notification Message-->

    <!--START: Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>
    <!--END: Loader-->
  </div>
</template>
          
<script>
// Imoprt libraries
import _ from "lodash";

// Importing components
import RegularHeader from "@/components/dashboard/RegularHeader";
import NotificationMessage from "@/components/modals/NotificationMessage";
import LineLoader from "@/components/loaders/LineLoader";
import PageLoader from "@/components/loaders/PageLoader";

// Importing Services
import { CompanyService, AuthService } from "@/services";

const SUPER_ADMIN_EMAIL = "hello@getmacha.com";

export default {
  name: "AccountSettings",
  data() {
    return {
      lodash: _,
      showOptions: {
        headerShadow: false,
        lineLoader: false,
      },
      regularHeader: {
        title: "Account",
        description: "Details of your account with Macha",
      },
      servers: [
        {
          title: "Ticket data & account settings",
          description:
            "Macha has a zero-data retention policy and stores only the answers and analysis created by agents as data for tickets. We don't share any customer data, ticket comments or information.",
          location: "Frankfurt, Germany [EU]",
          country: "germany",
        },
        {
          title: "Knowledge sources",
          description:
            "This is data which you've chosen to use as knowledge for the AI. These are securely stored in vector format and typically don't contain any personal information.",
          location: "Frankfurt, Germany [EU]",
          country: "germany",
        },
      ],
      status: {
        show: false,
        status: "success",
        title: "We've receieved your request. Our team will reach out to you.",
      },
      hasPassword: false,
      passwordForm: {
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      },
      adminPasswordForm: {
        targetEmail: "",
        newPassword: "",
      },
    };
  },
  props: {},
  watch: {},
  components: {
    RegularHeader,
    NotificationMessage,
    LineLoader,
    PageLoader,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    currentUserEmail() {
      return this.$store.getters.user?.user?.email || "";
    },
    isSuperAdmin() {
      return this.currentUserEmail.toLowerCase() === SUPER_ADMIN_EMAIL;
    },
    canUpdatePassword() {
      const { newPassword, confirmPassword, oldPassword } = this.passwordForm;
      if (this.hasPassword) {
        return (
          oldPassword.length >= 1 &&
          newPassword.length >= 6 &&
          newPassword === confirmPassword
        );
      }
      return newPassword.length >= 6 && newPassword === confirmPassword;
    },
    canSetUserPassword() {
      const { targetEmail, newPassword } = this.adminPasswordForm;
      return targetEmail.length > 0 && newPassword.length >= 6;
    },
  },
  async mounted() {
    this.settingsScroll();
    await this.checkHasPassword();
  },
  methods: {
    // Check if current user has a password set
    async checkHasPassword() {
      if (this.currentUserEmail) {
        const response = await AuthService.CheckHasPassword({
          email: this.currentUserEmail,
        });
        if (!response.hasError && response.data) {
          this.hasPassword = response.data.hasPassword;
        }
      }
    },

    // Update own password
    async updateOwnPassword() {
      this.showOptions.lineLoader = true;

      const data = {
        oldPassword: this.passwordForm.oldPassword,
        newPassword: this.passwordForm.newPassword,
      };

      const response = await AuthService.UpdatePassword(data);

      this.showOptions.lineLoader = false;

      if (response.hasError) {
        this.status.status = "error";
        this.status.title = response.message;
      } else {
        this.status.status = "success";
        this.status.title = response.message;
        this.hasPassword = true;
        this.passwordForm = {
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        };
      }

      this.showStatusMessage(this.status, 2500);
    },

    // Set password for another user (admin only)
    async setUserPassword() {
      this.showOptions.lineLoader = true;

      const data = {
        targetEmail: this.adminPasswordForm.targetEmail,
        newPassword: this.adminPasswordForm.newPassword,
      };

      const response = await AuthService.SetPassword(data);

      this.showOptions.lineLoader = false;

      if (response.hasError) {
        this.status.status = "error";
        this.status.title = response.message;
      } else {
        this.status.status = "success";
        this.status.title = response.message;
        this.adminPasswordForm = {
          targetEmail: "",
          newPassword: "",
        };
      }

      this.showStatusMessage(this.status, 2500);
    },

    // Get logo of country
    getCountryFlag(country) {
      let iconURL = `/assets/images/flags/${country}.jpg`;
      return iconURL;
    },

    // Send a notification to team for account actions
    async sendNotification(actionType) {
      this.showOptions.lineLoader = true;

      const data = { actionType };
      await CompanyService.SendAccountRequest(data);

      this.showOptions.lineLoader = false;
      this.showStatusMessage(this.status, 2500);
    },

    // Event on settingsScroll scroll
    settingsScroll() {
      const scrollTop = this.$refs.settings.scrollTop;

      // Check if header needs shadow
      if (scrollTop > 0) this.showOptions.headerShadow = true;
      else this.showOptions.headerShadow = false;
    },
  },
};
</script>
          
<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";
.header-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 0 !important;
  padding-bottom: 1rem;
  z-index: 2;

  .regular-header {
    flex: 1;
    max-width: 70%;
    margin-right: 30%;
  }
}

.page-loader-wrapper {
  margin-top: 2rem;
}

/deep/ .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}

.show-shadow {
  box-shadow: 0 0.5rem 0.7rem -0.5rem $boxShadowColor;
}

.info-wrapper {
  margin-bottom: 2rem;

  h3 {
    font-family: $titleFont;
    font-size: $largerFontSize;
    font-weight: $boldFontWeight;
  }

  p {
    font-size: $smallFontSize;
    opacity: $lightOpacity;
  }
}

.server-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-left: 1rem;

  .server-info {
    flex: 1;
    margin-right: 25%;
    margin-bottom: 2rem;

    h3 {
      font-size: $normalFontSize;
      margin-bottom: 1rem;
    }

    p {
      opacity: $lightOpacity;
      font-size: $smallFontSize;
    }
  }
}

.server-location {
  font-weight: 600;
  display: inline-block;
  font-size: $smallerFontSize;
  background-color: $emeraldColor;
  color: lighten($emeraldColor, 50%);
  border: 1px solid lighten($emeraldColor, 20%);
  border-radius: 0.5rem;
  font-weight: $mediumFontWeight;
  padding: 0.25rem 0.85rem;
  margin-right: 0.75rem;
}

.server-details {
  display: flex;
  flex-direction: row;
  align-items: center;

  img {
    width: 2rem;
    border-radius: 0.25rem;
  }
}

/deep/ .notification-message {
  bottom: auto;
  width: calc(100% - 5rem);
  top: 1.5rem;
  justify-content: center;
  background-color: $emeraldColor;
  border: 1px solid lighten($emeraldColor, 20%);

  .info-wrapper {
    h3 {
      color: darken($emeraldColor, 40%);
    }
  }

  .unicon svg {
    fill: darken($emeraldColor, 40%);
  }
}

.account-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  .btn-primary {
    background-color: lighten($redColor, 37.5%);
    color: darken($redColor, 30%);
    border: 1px solid lighten($redColor, 15%);

    &:hover {
      background-color: lighten($redColor, 30.5%);
      border: 1px solid darken($redColor, 25%);
    }
  }
}

.server-location-wrapper.card {
  margin: 0;
}

.security-document {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0.75rem 0 2rem;

  /deep/ {
    .unicon svg {
      fill: $greyColor;
      width: 1rem;
      margin-right: 0.5rem;
    }
  }

  span {
    font-size: $smallFontSize;
    color: $greyColor;
  }

  a {
    color: $purpleColor;
    font-weight: 500;
  }
}
.settings-scroll-block {
  padding: 1.5rem 0 3rem;
  overflow-y: auto;
}

.password-management-wrapper {
  margin-top: 2rem;

  .password-form {
    margin-left: 1rem;
    margin-bottom: 2rem;

    h4 {
      font-size: $normalFontSize;
      margin-bottom: 1rem;
    }

    .form-group {
      margin-bottom: 1rem;

      label {
        display: block;
        font-size: $smallFontSize;
        margin-bottom: 0.5rem;
        opacity: $lightOpacity;
      }

      input {
        width: 100%;
        max-width: 300px;
        padding: 0.75rem 1rem;
        border: 1px solid $inputBorderColor;
        border-radius: 0.5rem;
        font-size: $smallFontSize;

        &:focus {
          outline: none;
          border-color: $purpleColor;
        }
      }
    }

    .btn {
      margin-top: 0.5rem;
    }

    &.admin-password-form {
      margin-top: 2rem;
      padding-top: 2rem;
      border-top: 1px solid $inputBorderColor;

      .admin-note {
        font-size: $smallFontSize;
        color: $greyColor;
        margin-bottom: 1rem;
      }
    }
  }
}
</style>